import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  formatK,
  type GibbsStrings,
  gibbsMessages,
} from "@/components/tools/chem-physical/gibbs-panel";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { GAS_CONSTANT } from "@/lib/tool-kit/chem-physical/constants";
import {
  type EquilibriumMode,
  type EquilibriumSolution,
  MOLAR_ENERGY_UNITS,
  solveEquilibrium,
} from "@/lib/tool-kit/chem-physical/gibbs";
import {
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import {
  TEMPERATURE_UNITS,
  temperatureSymbol,
} from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const DEFAULT_SIG = 3;
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const MODES: EquilibriumMode[] = ["k", "dg0", "dg"];

function EquilibriumAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: EquilibriumSolution;
  texts: { dg0: string; k: string; q: string; t: string };
  units: { dg0: string; dg: string; t: string };
  sig: number;
  strings: GibbsStrings;
}) {
  const fmt = useToolNumber();
  const { mode } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const kText = formatK(fmt, solution.log10K, sig);
  const rt = GAS_CONSTANT * solution.kelvin;
  const energy = (value: number, unit: string) =>
    `${formatSig(fmt, value / unitFactor(MOLAR_ENERGY_UNITS, unit), sig)} ${unitSymbol(MOLAR_ENERGY_UNITS, unit)}`;
  const answers = {
    k: { label: strings.equilibriumConstant, text: kText },
    dg0: {
      label: strings.standardDeltaG,
      text: energy(solution.dg0, units.dg0),
    },
    dg: {
      label: strings.deltaGNow,
      text: energy(solution.dg ?? 0, units.dg),
    },
  }[mode];
  const conversions: string[] = [];
  if (mode !== "dg0" && unitFactor(MOLAR_ENERGY_UNITS, units.dg0) !== 1) {
    conversions.push(
      t(strings.convertLine, {
        label: strings.standardDeltaG,
        value: texts.dg0.trim(),
        unit: unitSymbol(MOLAR_ENERGY_UNITS, units.dg0),
        converted: `${p(solution.dg0)} J/mol`,
      })
    );
  }
  if (units.t !== "K") {
    conversions.push(
      t(strings.convertLine, {
        label: strings.termT,
        value: texts.t.trim(),
        unit: temperatureSymbol(units.t),
        converted: `${p(solution.kelvin)} K`,
      })
    );
  }
  const direction = {
    forward: strings.dirForward,
    reverse: strings.dirReverse,
    equilibrium: strings.dirEquilibrium,
  };
  const meaning =
    solution.log10K > 0 ? strings.meaningLarge : strings.meaningSmall;
  const lines = {
    k: [
      strings.eqK,
      t(strings.eqKRt, { rt: p(rt) }),
      t(strings.eqKLn, {
        dg0: p(solution.dg0),
        rt: p(rt),
        lnk: p(solution.lnK),
      }),
      t(strings.eqKResult, {
        lnk: p(solution.lnK),
        k: kText,
        log: formatSig(fmt, solution.log10K, sig + 1),
      }),
    ],
    dg0: [
      strings.eqDg0,
      t(strings.eqKRt, { rt: p(rt) }),
      t(strings.eqDg0Result, {
        rt: p(rt),
        k: texts.k.trim(),
        dg0: p(solution.dg0),
      }),
    ],
    dg: [
      strings.eqDg,
      t(strings.eqKRt, { rt: p(rt) }),
      t(strings.eqDgResult, {
        dg0: p(solution.dg0),
        rt: p(rt),
        q: texts.q.trim(),
        dg: p(solution.dg ?? 0),
      }),
      t(strings.eqDgK, { k: kText }),
    ],
  }[mode];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={answers.label} value={answers.text} />
        {mode === "k" ? null : (
          <StatCard label={strings.equilibriumConstant} value={kText} />
        )}
        <StatCard
          label={strings.log10K}
          value={formatSig(fmt, solution.log10K, sig)}
        />
        {solution.direction === null ? null : (
          <StatCard
            label={strings.reactionDirection}
            value={direction[solution.direction]}
          />
        )}
        {mode === "dg" ? null : (
          <StatCard
            label={strings.standardDeltaG}
            value={energy(solution.dg0, units.dg0)}
          />
        )}
      </StatGrid>
      <Notice>{meaning}</Notice>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{strings.rNote}</p>
      </div>
      <p className="text-muted-foreground text-sm">{strings.standardNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: answers.label,
          value: answers.text,
        })}
      />
    </>
  );
}

export function GibbsEquilibriumPanel({
  strings,
  sigChoice,
}: {
  strings: GibbsStrings;
  sigChoice: string;
}) {
  const [mode, setMode] = useState<EquilibriumMode>("k");
  const [text, setText] = useState({
    dg0: "55.7",
    k: "",
    q: "1.0e-5",
    t: "298.15",
  });
  const [unit, setUnit] = useState({ dg0: "kJ", dg: "kJ", t: "K" });
  const outcome = solveEquilibrium({ mode, text, unit });
  const typed = {
    k: [text.dg0, text.t],
    dg0: [text.k, text.t],
    dg: [text.dg0, text.q, text.t],
  }[mode];
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<string, string> = {
    dg0: strings.standardDeltaG,
    k: strings.equilibriumConstant,
    q: strings.reactionQuotient,
    t: strings.termT,
  };
  const modeNames: Record<EquilibriumMode, string> = {
    k: strings.modeFindK,
    dg0: strings.modeFindDg0,
    dg: strings.modeFindDg,
  };
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="gfe-eq-mode"
        onChange={setMode}
        options={MODES.map((id) => ({ value: id, label: modeNames[id] }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityInput
          id="gfe-dg0"
          label={
            mode === "dg0"
              ? t(strings.answerSuffix, { label: strings.standardDeltaG })
              : strings.standardDeltaG
          }
          onUnitChange={(value) => setUnit({ ...unit, dg0: value })}
          onValueChange={(value) => setText({ ...text, dg0: value })}
          solved={mode === "dg0"}
          unit={unit.dg0}
          unitLabel={strings.unitEnergyG}
          units={unitOptionsOf(MOLAR_ENERGY_UNITS)}
          value={text.dg0}
        />
        {mode === "dg0" ? (
          <TextField
            hint={strings.kHint}
            id="gfe-k"
            label={strings.equilibriumConstant}
            numeric
            onChange={(value) => setText({ ...text, k: value })}
            value={text.k}
          />
        ) : null}
        {mode === "dg" ? (
          <>
            <TextField
              hint={strings.qHint}
              id="gfe-q"
              label={strings.reactionQuotient}
              numeric
              onChange={(value) => setText({ ...text, q: value })}
              value={text.q}
            />
            <QuantityInput
              id="gfe-dg"
              label={t(strings.answerSuffix, { label: strings.deltaGNow })}
              onUnitChange={(value) => setUnit({ ...unit, dg: value })}
              onValueChange={() => undefined}
              solved
              unit={unit.dg}
              unitLabel={strings.unitEnergyG}
              units={unitOptionsOf(MOLAR_ENERGY_UNITS)}
              value=""
            />
          </>
        ) : null}
        <QuantityInput
          id="gfe-eq-t"
          label={strings.termT}
          onUnitChange={(value) => setUnit({ ...unit, t: value })}
          onValueChange={(value) => setText({ ...text, t: value })}
          unit={unit.t}
          unitLabel={strings.unitTemperature}
          units={TEMPERATURE_OPTIONS}
          value={text.t}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <EquilibriumAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={gibbsMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
